// Per-device fallback for members whose direct browser connection to Recharge
// fails at the network level (established HTTP/3 connections dying mid-fetch
// surface as WebKit "Load failed" — see LOGIN_ISSUE_INVESTIGATION.md).
//
// Default is the normal direct SDK connection. When a login call fails with a
// network-level error, we flag the device and re-init the SDK against
// wildgrain-api's /api/recharge-proxy passthrough, which re-points BOTH
// Recharge hosts (admin + api) at a first-party TCP-only path. The flag
// expires so devices return to the direct path once the network issue passes.
import { initRecharge } from '@rechargeapps/storefront-client';
import { BASE_API_URL } from '../services/api';

const FLAG_KEY = 'rechargeProxyModeSince';
const FLAG_MAX_AGE_MS = 14 * 24 * 60 * 60 * 1000;

export const isProxyMode = () => {
  try {
    const since = Number(localStorage.getItem(FLAG_KEY));
    return since > 0 && Date.now() - since < FLAG_MAX_AGE_MS;
  } catch (err) {
    return false;
  }
};

export const enableProxyMode = () => {
  try {
    localStorage.setItem(FLAG_KEY, String(Date.now()));
  } catch (err) {
    // Storage unavailable: proxy mode simply won't stick beyond this re-init.
  }
};

// A fetch that died before any HTTP response existed (TypeError: "Load
// failed" / "Failed to fetch") — never a real Recharge rejection, which
// arrives as RechargeRequestError or a plain Error with the API's message.
export const isNetworkLevelError = (error) =>
  error instanceof TypeError ||
  /load failed|failed to fetch|network connection was lost/i.test(String(error?.message || ''));

// HTTP 502/504 answered by a gateway in front of the real upstream —
// Recharge's load balancer on the direct path, or our own passthrough when
// it can't reach Recharge. Transient edge failures, worth one retry via the
// other path. The SDK's wording for these is fixed in request.js.
export const isGatewayError = (error) =>
  /gateway error occurred/i.test(String(error?.message || ''));

export const isRetriableTransportError = (error) =>
  isNetworkLevelError(error) || isGatewayError(error);

// On a transport failure (the "Load failed" class — connection died before
// any HTTP response — or a 502/504 gateway error from Recharge's edge), flag
// this device onto the first-party proxy path, re-init the SDK, and retry the
// call once. Real Recharge rejections (wrong email, bad auth) pass straight
// through untouched. Used by the login page and the magic-link redirect.
export const withProxyFallback = async (call) => {
  try {
    return await call();
  } catch (error) {
    if (!isRetriableTransportError(error) || isProxyMode()) throw error;
    enableProxyMode();
    initRechargeWithMode();
    return await call();
  }
};

// Single init path for the whole app: same options as before, plus the
// proxy re-pointing when this device is flagged. environment 'preprod' +
// environmentUri only change the SDK's base URLs (admin -> uri, api ->
// uri/api); CDN stays prod and Spreedly config comes from API response data.
export const initRechargeWithMode = (options = {}) => {
  initRecharge({
    storeIdentifier: process.env.REACT_APP_STORE_IDENTIFIER,
    storefrontAccessToken: process.env.REACT_APP_STOREFRONT_ACCESS_TOKEN,
    appName: 'WildgrainMemberPortal',
    appVersion: '1.0.0',
    ...options,
    ...(isProxyMode()
      ? {
          environment: 'preprod',
          environmentUri: `${BASE_API_URL}/api/recharge-proxy`,
        }
      : {}),
  });
};
